import { MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
import { getSceneLicenseBlockReason } from "@utils/scene-license-utils";
import { getXgSeatBlockReason } from "@utils/xg-seat-utils";

/**
 * Why a seat the member holds already cannot be changed. It is not shown, since the card of the seat
 * says "Assigned" instead.
 */
export const HELD_SEAT_REASON = "Already holds this seat";

/** What the user is told when the workspace has no seat of the kind left to give */
export const NO_FREE_SEAT_REASON = "No seats remaining";

/** A seat that can be listed to assign to a member or to take away from them */

/**
 * Returns the seats to list for a member, with whether each of them can be given to the member.
 *
 * The Sphere XG seat is only listed for a plan with assigned seats. The Sphere XG seat that comes
 * with being an active user is never listed, since it follows the roles of the member. The SCENE
 * seats are only listed for a workspace that owns some.
 */
export function getSeatOptions(props) {
  const options = [];
  if (props.isActiveSeatPlan) {
    options.push(getXgSeatOption(props));
  }
  if (props.areSceneLicensesAvailable) {
    options.push(getSceneSeatOption(props));
  }
  return options;
}

/** Whether the user cannot change whether the member holds the seat */
export function isSeatLocked({
  blockReason
}) {
  return !!blockReason;
}

/** Whether the seat cannot be given to the member only because the workspace has none left */
export function isSeatSoldOut({
  blockReason
}) {
  return blockReason === NO_FREE_SEAT_REASON;
}

/** Whether any of the seats is worth offering to assign, see SeatOption.isOffered */
export function shouldOfferToAssignSeats(options) {
  return options.some(({
    isOffered
  }) => isOffered);
}

/**
 * Whether the user could change any of the seats of the member: assign one that is worth offering,
 * or take away one the member holds.
 */
export function shouldOfferToManageSeats(options) {
  return options.some(option => option.isOffered || option.isHeld && !isSeatLocked(option));
}

/** Returns the Sphere XG seat to list, which can be assigned but never taken away again */
function getXgSeatOption({
  member,
  heldSeats,
  canManageXgSeats,
  xgSeatsAvailable
}) {
  if (heldSeats.hasXgSeat) {
    return getHeldSeatOption("xgSeat");
  }
  const blockReason = canManageXgSeats ? getXgSeatBlockReason(member) : getMissingPermissionReason("xgSeat");
  return getUnheldSeatOption("xgSeat", blockReason, xgSeatsAvailable);
}

/** Returns the SCENE seat to list */
function getSceneSeatOption({
  member,
  heldSeats,
  canManageSceneLicenses,
  sceneSeatsAvailable
}) {
  if (heldSeats.hasSceneSeat) {
    // Unlike the Sphere XG seat, the SCENE seat can be taken away again to free it for somebody else
    return canManageSceneLicenses ? {
      seat: "sceneSeat",
      isHeld: true,
      isOffered: false
    } : getHeldSeatOption("sceneSeat");
  }
  const blockReason = canManageSceneLicenses ? getSceneLicenseBlockReason(member) : getMissingPermissionReason("sceneSeat");
  return getUnheldSeatOption("sceneSeat", blockReason, sceneSeatsAvailable);
}

/**
 * Returns a seat the member does not hold, which cannot be given to them for the block reason, or
 * while the workspace has none of them left
 */
function getUnheldSeatOption(seat, blockReason, seatsAvailable) {
  if (blockReason) {
    return {
      seat,
      isHeld: false,
      blockReason,
      isOffered: false
    };
  }

  // Offered nevertheless, so that the user learns that the seats ran out
  if (seatsAvailable < 1) {
    return {
      seat,
      isHeld: false,
      blockReason: NO_FREE_SEAT_REASON,
      isOffered: true
    };
  }
  return {
    seat,
    isHeld: false,
    isOffered: true
  };
}

/**
 * Returns a seat that the member holds already, which can therefore not be given again, nor be
 * taken away from them
 */
function getHeldSeatOption(seat) {
  return {
    seat,
    isHeld: true,
    blockReason: HELD_SEAT_REASON,
    isOffered: false
  };
}

/** Returns why the current user cannot assign a seat */
export function getMissingPermissionReason(seat) {
  return `You are not allowed to assign ${MEMBER_SEAT_LABELS[seat]} seats`;
}